// The draft uses document.modelContext + AbortSignal; early Chrome uses
// navigator.modelContext + unregisterTool. Never replace another provider's tools.
export function toolResult(value, isError = false) {
  return { content: [{ type: "text", text: JSON.stringify(value) }], ...(isError ? { isError: true } : {}) }
}

export function registerPageTools(tools, {
  document = globalThis.document,
  navigator = globalThis.navigator,
  isActive = () => true,
  authorize = async () => {},
  onError = (error) => console.warn("WebMCP registration unavailable", error)
} = {}) {
  const modern = typeof document?.modelContext?.registerTool === "function"
  const context = modern ? document.modelContext : navigator?.modelContext
  if (typeof context?.registerTool !== "function" || (!modern && typeof context.unregisterTool !== "function")) {
    return { ready: Promise.resolve(), dispose() {} }
  }

  const lifetime = new AbortController()
  const registered = new Set()
  const unregister = (name) => {
    if (!registered.delete(name) || modern) return
    try {
      Promise.resolve(context.unregisterTool(name)).catch(onError)
    } catch (error) {
      onError(error)
    }
  }

  const ready = Promise.all(tools.map(async (tool) => {
    try {
      const definition = {
        ...tool,
        execute: async (input = {}, options) => {
          if (lifetime.signal.aborted || !isActive() || options?.signal?.aborted) {
            return toolResult({ error: "This page is no longer active or the call was cancelled." }, true)
          }
          try {
            const argumentsCopy = structuredClone(input)
            const signal = options?.signal
              ? AbortSignal.any([lifetime.signal, options.signal])
              : lifetime.signal
            await authorize(signal)
            if (signal.aborted || !isActive()) throw new Error("This page is no longer active or the call was cancelled.")
            return await tool.execute(argumentsCopy, { signal })
          } catch (error) {
            return toolResult({ error: error.message }, true)
          }
        }
      }
      if (modern) {
        await context.registerTool(definition, { signal: lifetime.signal })
      } else {
        await context.registerTool(definition)
      }
      registered.add(tool.name)
      if (lifetime.signal.aborted) unregister(tool.name)
    } catch (error) {
      if (!lifetime.signal.aborted) onError(error)
    }
  }))

  return {
    ready,
    dispose() {
      lifetime.abort()
      for (const name of registered) unregister(name)
    }
  }
}
